import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormField, FormInput, FormSelect } from '@practium/ui';
import type { AreaFormData, PropertyPageMode } from '../../../types';
import {
	CULTURE_LABELS,
	CULTURE_OPTIONS,
	IRRIGATION_LABELS,
	IRRIGATION_OPTIONS,
} from '../../../constants/properties.constants';
import { useAreaForm } from '../../../hooks/useAreaForm';
import styles from '../PropertyForm.module.css';

export interface AreaFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

interface AreaFormProps {
	mode: PropertyPageMode;
	propId: string;
	propertyName: string;
	initialData?: AreaFormData;
	onSubmit: (createdId?: string) => void;
	onLoadingChange: (loading: boolean) => void;
	avatarFile?: File | null;
}

export const AreaForm = forwardRef<AreaFormRef, AreaFormProps>(function AreaForm(
	{ mode, propId, propertyName, initialData, onSubmit, onLoadingChange, avatarFile },
	ref
) {
	const { form, errors, isLoading, set, handleSubmit, isDirty } = useAreaForm({
		propId,
		initialData,
		onSubmit,
		avatarFile,
	});

	useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

	useEffect(() => {
		onLoadingChange(isLoading);
	}, [isLoading, onLoadingChange]);

	const isView = mode === 'view';

	if (isView) {
		return (
			<div className={styles.viewGrid}>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Nome</span>
					<span className={styles.viewValue}>{form.name || '—'}</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Cultura</span>
					<span className={styles.viewValue}>{CULTURE_LABELS[form.culture] ?? '—'}</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Irrigação</span>
					<span className={styles.viewValue}>{IRRIGATION_LABELS[form.irrigation] ?? '—'}</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Propriedade</span>
					<span className={styles.viewValue}>{propertyName || '—'}</span>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.formGrid}>
			<FormField label="Nome" required error={errors.name}>
				<FormInput
					value={form.name}
					onChange={(e) => set('name', e.target.value)}
					placeholder="Ex.: AJ-45"
					hasError={!!errors.name}
				/>
			</FormField>
			<FormField label="Cultura" required error={errors.culture}>
				<FormSelect
					value={form.culture}
					onChange={(v) => set('culture', v)}
					options={CULTURE_OPTIONS}
					placeholder="Selecione"
					hasError={!!errors.culture}
				/>
			</FormField>
			<FormField label="Irrigação" required error={errors.irrigation}>
				<FormSelect
					value={form.irrigation}
					onChange={(v) => set('irrigation', v)}
					options={IRRIGATION_OPTIONS}
					placeholder="Selecione"
					hasError={!!errors.irrigation}
				/>
			</FormField>
			<FormField label="Propriedade">
				<FormInput value={propertyName} disabled className={styles.readonlyInput} />
			</FormField>
		</div>
	);
});
